import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ArrowRight, CreditCard, Banknote } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ShippingForm, emptyShipping, validateShipping } from "@/components/ShippingForm";
import { OrderSummary } from "./Cart";

export default function Checkout() {
  const { t } = useTranslation();
  const { items, clear } = useCart();
  const { user } = useAuth();
  const [f, setF] = useState(() => (user ? { ...emptyShipping, ...user } : emptyShipping));
  useEffect(() => { if (user) setF((p) => ({ ...emptyShipping, ...user, ...Object.fromEntries(Object.entries(p).filter(([, v]) => v)) })); }, [user]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [method, setMethod] = useState("card");

  const place = async () => {
    const v = validateShipping(f);
    if (v) return setErr(v);
    setErr(null);
    setLoading(true);
    try {
      const payload = { shipping: f, items: items.map((i) => ({ code: i.code, qty: i.qty })) };
      if (method === "card") {
        const { data } = await api.post("/payments/checkout", { ...payload, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      const { data } = await api.post("/orders", payload);
      clear();
      setDone(data);
    } catch (e) {
      setErr("errGeneric");
    } finally {
      setLoading(false);
    }
  };

  if (done) return (
    <main className="nv-container py-24 text-center">
      <div className="nv-fade-up mx-auto max-w-lg rounded-3xl bg-black p-12 text-white">
        <CheckCircle2 size={56} className="mx-auto text-[#c6ff00]" />
        <h1 data-testid="order-success" className="mt-6 font-display text-3xl font-bold sm:text-4xl">{t("checkout.success")}</h1>
        <p className="mt-3 text-white/70">{t("checkout.successSub", { name: done.shipping.firstName, email: done.email })}</p>
        <p className="mt-6 text-xs uppercase tracking-widest text-white/50">{t("checkout.orderNo")}</p>
        <p data-testid="order-number" className="font-display text-2xl font-bold text-[#c6ff00]">{done.id}</p>
        <Link to="/" data-testid="order-back-home" className="nv-btn-lime mt-8">{t("checkout.backHome")} <ArrowRight size={16} /></Link>
      </div>
    </main>
  );

  if (items.length === 0) return <Navigate to="/cart" replace />;

  return (
    <main className="nv-container py-12">
      <h1 className="font-display text-4xl font-bold sm:text-5xl">{t("checkout.title")}</h1>
      <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_400px]">
        <section>
          {!user && <p className="mb-6 rounded-xl bg-[#f4f5f0] px-4 py-3 text-sm">{t("checkout.loginHint")} <Link to="/login" state={{ from: "/checkout" }} data-testid="checkout-login-link" className="font-bold underline">{t("auth.login")}</Link></p>}
          <h2 className="font-display text-xl font-bold">{t("checkout.shippingAddr")}</h2>
          <div className="mt-5"><ShippingForm form={f} onChange={(k, v) => setF((p) => ({ ...p, [k]: v }))} /></div>
          <h2 className="mt-10 font-display text-xl font-bold">{t("checkout.payment")}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2" role="radiogroup">
            {[["card", CreditCard, t("checkout.payCard"), t("checkout.payCardSub")], ["cod", Banknote, t("checkout.payCod"), t("checkout.payCodSub")]].map(([id, Icon, label, sub]) => (
              <button key={id} type="button" role="radio" aria-checked={method === id} data-testid={`pay-method-${id}`} onClick={() => setMethod(id)} className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-colors ${method === id ? "border-black bg-[#c6ff00]" : "border-black/10 hover:border-black/40"}`}>
                <Icon size={22} className="mt-0.5 shrink-0" />
                <span><span className="block font-display font-bold">{label}</span><span className="block text-xs text-black/60">{sub}</span></span>
              </button>
            ))}
          </div>
          {method === "card" && <p className="mt-3 text-xs text-black/50">{t("checkout.stripeNote")}</p>}
          {err && <p data-testid="checkout-error" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{t(`auth.${err}`)}</p>}
        </section>
        <OrderSummary cta={loading ? t("checkout.placing") : method === "card" ? t("checkout.payNow") : t("checkout.place")} onCta={place} loading={loading} testId="checkout-summary" />
      </div>
    </main>
  );
}
